/**
 * Single Unit Community Hangar Template
 *
 * The simplest building template - one building equals one unit.
 * Like a studio apartment: no internal divisions, the building
 * footprint IS the usable space.
 */

import { z } from 'zod'
import { Building2 } from 'lucide-react'
import type {
  BuildingTemplate,
  BaseTemplateParams,
  DoorWall,
  DoorType,
  GeneratedBuilding,
  GeneratedUnit,
  UnitEdge,
  DoorSpec,
} from './types'

/** Door spec schema for validation */
const doorSpecSchema = z.object({
  offset_ft: z.number().min(0, 'Offset must be positive'),
  width_ft: z.number().min(10, 'Min 10 ft').max(200, 'Max 200 ft'),
  height_ft: z.number().min(8, 'Min 8 ft').max(50, 'Max 50 ft'),
  type: z.enum(['bifold', 'sliding', 'hydraulic', 'rolling']),
})

/** Check if two doors overlap on a wall */
function doorsOverlap(
  door1: { offset_ft: number; width_ft: number },
  door2: { offset_ft: number; width_ft: number }
): boolean {
  // offset_ft is the leading edge (left side) of the door
  const door1Left = door1.offset_ft
  const door1Right = door1.offset_ft + door1.width_ft
  const door2Left = door2.offset_ft
  const door2Right = door2.offset_ft + door2.width_ft

  // Check for overlap (with 1ft minimum gap)
  const minGap = 1
  return !(door1Right + minGap <= door2Left || door2Right + minGap <= door1Left)
}

/** Validate doors on a wall don't overlap and fit within wall bounds */
function validateWallDoors(
  doors: Array<{ offset_ft: number; width_ft: number }>,
  wallLength: number
): { valid: boolean; errors: string[] } {
  const errors: string[] = []

  // Check each door fits within wall bounds
  // offset_ft is the leading edge (left side) of the door
  for (let i = 0; i < doors.length; i++) {
    const door = doors[i]
    const leftEdge = door.offset_ft
    const rightEdge = door.offset_ft + door.width_ft

    if (leftEdge < 0) {
      errors.push(`Door ${i + 1} extends past wall start`)
    }
    if (rightEdge > wallLength) {
      errors.push(`Door ${i + 1} extends past wall end`)
    }
  }

  // Check for overlapping doors
  for (let i = 0; i < doors.length; i++) {
    for (let j = i + 1; j < doors.length; j++) {
      if (doorsOverlap(doors[i], doors[j])) {
        errors.push(`Doors ${i + 1} and ${j + 1} overlap`)
      }
    }
  }

  return { valid: errors.length === 0, errors }
}

/** Doors config schema - multiple doors per wall */
const doorsConfigSchema = z.object({
  front: z.array(doorSpecSchema),
  back: z.array(doorSpecSchema),
  left: z.array(doorSpecSchema),
  right: z.array(doorSpecSchema),
})

/** Doors config type (extracted from schema) */
export type DoorsConfig = z.infer<typeof doorsConfigSchema>

/** Export validation helper for use in components */
export { validateWallDoors }

/** Parameters specific to single unit community template */
export interface SingleUnitParams extends BaseTemplateParams {
  width_ft: number
  depth_ft: number
  height_ft: number
  wall_thickness_ft: number  // Wall thickness for exterior walls
  doors: DoorsConfig  // Multi-door support
}

/** Form schema for single unit community template */
export const singleUnitFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  code: z.string().min(1, 'Building code is required').max(20, 'Code too long'),
  width_ft: z.number().min(20, 'Min 20 ft').max(500, 'Max 500 ft'),
  depth_ft: z.number().min(20, 'Min 20 ft').max(500, 'Max 500 ft'),
  height_ft: z.number().min(10, 'Min 10 ft').max(100, 'Max 100 ft'),
  elevation_ft: z.number().default(-10), // Vertical offset (negative = below ground)
  wall_thickness_ft: z.number().min(0.25).max(2).default(0.5),
  doors: doorsConfigSchema,
  floor_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color'),
  wall_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color'),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  bearing_deg: z.number().transform(v => ((v % 360) + 360) % 360),
})

/** Default values for single unit community template */
export const singleUnitDefaultValues: SingleUnitParams = {
  name: 'New Hangar',
  code: '', // Auto-generated based on building type (e.g., COM-001)
  width_ft: 100,
  depth_ft: 80,
  height_ft: 24,
  wall_thickness_ft: 0.5,  // 6 inches default
  doors: {
    front: [{
      // offset_ft is leading edge; to center 60ft door on 100ft wall: (100-60)/2 = 20
      offset_ft: 20,
      width_ft: 60,
      height_ft: 20,
      type: 'bifold',
    }],
    back: [],
    left: [],
    right: [],
  },
  floor_color: '#808080',
  wall_color: '#A0A0A0',
  latitude: 0,
  longitude: 0,
  bearing_deg: 0,
}

/** Helper to flatten DoorsConfig to door array for storage */
function flattenDoors(doors: DoorsConfig): Array<{
  wall: DoorWall
  width_ft: number
  height_ft: number
  offset_ft: number
  type: DoorType
}> {
  const result: Array<{
    wall: DoorWall
    width_ft: number
    height_ft: number
    offset_ft: number
    type: DoorType
  }> = []

  for (const wall of ['front', 'back', 'left', 'right'] as DoorWall[]) {
    for (const door of doors[wall]) {
      result.push({
        wall,
        width_ft: door.width_ft,
        height_ft: door.height_ft,
        offset_ft: door.offset_ft,
        type: door.type,
      })
    }
  }
  return result
}

/** Get primary door dimensions (first door found) for legacy fields */
function getPrimaryDoor(doors: DoorsConfig): { wall: DoorWall; width: number; height: number } | null {
  for (const wall of ['front', 'back', 'left', 'right'] as DoorWall[]) {
    if (doors[wall].length > 0) {
      return {
        wall,
        width: doors[wall][0].width_ft,
        height: doors[wall][0].height_ft,
      }
    }
  }
  return null
}

/** Generate building configuration from parameters */
function generateBuilding(params: SingleUnitParams): GeneratedBuilding {
  // Convert bearing from degrees to radians
  const rotationRadians = (params.bearing_deg * Math.PI) / 180

  // Flatten doors for storage
  const flatDoors = flattenDoors(params.doors)

  // Get primary door for legacy fields (backwards compatibility)
  const primaryDoor = getPrimaryDoor(params.doors)

  return {
    name: params.name,
    code: params.code,
    type: 'single_unit_hangar',
    exterior_width: params.width_ft, // X-axis (door-parallel)
    exterior_depth: params.depth_ft, // Y-axis (front-to-back)
    exterior_height: params.height_ft,
    latitude: params.latitude,
    longitude: params.longitude,
    rotation: rotationRadians,
    door_width: primaryDoor?.width || 60,
    door_height: primaryDoor?.height || 20,
    settings: {
      floor_color: params.floor_color,
      wall_color: params.wall_color,
      doors: flatDoors,
    },
    type_config: {
      template: 'single_unit_community',
      doors: params.doors, // Store full DoorsConfig for edit
      door_positions: flatDoors.map(d => ({
        side: d.wall,
        width: d.width_ft,
        offset: d.offset_ft,
      })),
      max_aircraft: Math.floor((params.width_ft * params.depth_ft) / 2000), // Rough estimate
    },
  }
}

/** Generate units for the building (single unit for this template) */
function generateUnits(
  _building: GeneratedBuilding,
  params: SingleUnitParams
): GeneratedUnit[] {
  // Convert DoorsConfig arrays to DoorSpec arrays for each wall
  const convertToDoorSpecs = (doors: Array<{ offset_ft: number; width_ft: number; height_ft: number; type: DoorType }>): DoorSpec[] => {
    return doors.map(d => ({
      offset_ft: d.offset_ft,
      width_ft: d.width_ft,
      height_ft: d.height_ft,
      type: d.type,
    }))
  }

  // Generate edges for the single unit (all exterior walls)
  // Each wall gets its door openings from the DoorsConfig
  const edges: UnitEdge[] = [
    {
      side: 'front',
      type: 'exterior',
      canBeShared: false,
      openings: params.doors.front.length > 0 ? convertToDoorSpecs(params.doors.front) : undefined,
    },
    {
      side: 'back',
      type: 'exterior',
      canBeShared: false,
      openings: params.doors.back.length > 0 ? convertToDoorSpecs(params.doors.back) : undefined,
    },
    {
      side: 'left',
      type: 'exterior',
      canBeShared: false,
      openings: params.doors.left.length > 0 ? convertToDoorSpecs(params.doors.left) : undefined,
    },
    {
      side: 'right',
      type: 'exterior',
      canBeShared: false,
      openings: params.doors.right.length > 0 ? convertToDoorSpecs(params.doors.right) : undefined,
    },
  ]

  // Get primary door for legacy unit fields
  const primaryDoor = getPrimaryDoor(params.doors)

  return [
    {
      code: params.code, // Unit inherits building code
      type: 'conventional_hangar',
      capacity_strategy: 'stackable', // Multiple aircraft, free placement
      interior_width: params.width_ft,
      interior_depth: params.depth_ft,
      interior_height: params.height_ft,
      door_width: primaryDoor?.width || 60,
      door_height: primaryDoor?.height || 20,
      door_type: params.doors.front[0]?.type || params.doors.back[0]?.type || params.doors.left[0]?.type || params.doors.right[0]?.type || 'bifold',
      offset_x: 0, // Unit starts at building origin
      offset_y: 0,
      unit_rotation: 0, // Same rotation as building
      edges, // Edge classification for rendering and collision
    },
  ]
}

/** Single Unit Community Hangar Template */
export const singleUnitCommunityTemplate: BuildingTemplate<SingleUnitParams> = {
  id: 'single_unit_community',
  name: 'Single Unit Community Hangar',
  description: 'One building = one unit. Simple open floor plan for multiple aircraft.',
  icon: Building2,
  buildingType: 'single_unit_hangar',
  generateBuilding,
  generateUnits,
  formSchema: singleUnitFormSchema,
  defaultValues: singleUnitDefaultValues,
}
